import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  GraduationCap,
  UtensilsCrossed,
  ShoppingCart,
  Stethoscope,
  Users,
  Brain,
  Sparkles,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  RefreshCw,
  X,
  Moon,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

const DashboardPage = () => {
  const { user, family } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [lastBriefingFetch, setLastBriefingFetch] = useState(null);
  
  // AI Daily Insight state
  const [aiInsight, setAiInsight] = useState(null);
  const [aiInsightCallId, setAiInsightCallId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [insightExpanded, setInsightExpanded] = useState(false);

  const fetchBriefing = useCallback(async (force = false) => {
    // Check cache
    if (!force && lastBriefingFetch && (Date.now() - lastBriefingFetch) < CACHE_TTL && briefing) {
      return;
    }
    
    setBriefingLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/briefing`);
      setBriefing(response.data);
      setLastBriefingFetch(Date.now());
    } catch (error) {
      console.error('Briefing fetch error:', error);
    } finally {
      setBriefingLoading(false);
    }
  }, [lastBriefingFetch, briefing]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Summary fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchBriefing();
  }, []);

  const getAiDailyInsight = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/daily-insight`, {});
      setAiInsight(response.data.insight_text);
      setAiInsightCallId(response.data.call_id);
      setInsightExpanded(true);
    } catch (error) {
      toast.error('AI insight unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const confirmInsight = async (action) => {
    if (aiInsightCallId) {
      try {
        await axios.post(`${API}/ai/confirm`, {
          call_id: aiInsightCallId,
          user_action: action
        });
      } catch (error) {
        console.error('Failed to confirm insight:', error);
      }
    }
    setInsightExpanded(false);
    if (action === 'ignored') {
      setAiInsight(null);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Chaos Meter = overall busyness this week (open items vs healthy-week threshold).
  // Comes from the same calculation as the Load Map, so the two can never disagree.
  const hasLoadMapData = briefing?.chaos_meter_percentage !== null && briefing?.chaos_meter_percentage !== undefined;
  const chaosLevel = hasLoadMapData ? briefing.chaos_meter_percentage : 0;
  const openCount = briefing?.chaos_open_count ?? 0;

  const getChaosLabel = (level, hasLoadMap) => {
    if (!hasLoadMap) {
      return { label: 'Nothing to measure yet', color: 'text-nurture-muted', bg: 'bg-nurture-cream-dark', isPlaceholder: true };
    }
    if (briefing?.chaos_over_capacity) return { label: 'Over capacity', color: 'text-red-500', bg: 'bg-red-500', isPlaceholder: false };
    if (level <= 25) return { label: 'Zen Mode', color: 'text-nurture-sage', bg: 'bg-nurture-sage', isPlaceholder: false };
    if (level <= 50) return { label: 'Busy Bee', color: 'text-nurture-maize', bg: 'bg-nurture-maize', isPlaceholder: false };
    if (level <= 75) return { label: 'Juggling', color: 'text-orange-500', bg: 'bg-orange-500', isPlaceholder: false };
    return { label: 'Full Throttle', color: 'text-red-500', bg: 'bg-red-500', isPlaceholder: false };
  };

  const chaosInfo = getChaosLabel(chaosLevel, hasLoadMapData);
  const chaosSublabel = hasLoadMapData
    ? `${openCount} open this week`
    : 'Nothing open this week';

  const quickLinks = [
    { path: '/school', label: 'School', icon: GraduationCap, color: 'bg-blue-100 text-blue-600', count: summary?.school_tasks },
    { path: '/meals', label: 'Meals', icon: UtensilsCrossed, color: 'bg-orange-100 text-orange-600', count: summary?.meals_planned },
    { path: '/groceries', label: 'Grocery', icon: ShoppingCart, color: 'bg-green-100 text-green-600', count: summary?.low_stock_items },
    { path: '/appointments', label: 'Health', icon: Stethoscope, color: 'bg-purple-100 text-purple-600', count: summary?.upcoming_appointments },
    { path: '/handoff', label: 'Handoff', icon: Users, color: 'bg-pink-100 text-pink-600', count: summary?.pending_handoffs },
    { path: '/brain-dump', label: 'Brain Dump', icon: Brain, color: 'bg-indigo-100 text-indigo-600', count: summary?.unprocessed_brain_dumps },
  ];

  const handleChaosClick = () => {
    // Bug 1 Fix: Navigate to Load Map page
    window.location.href = '/load-map';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-40 w-full rounded-3xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-32 rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-nurture-navy">
            {getGreeting()}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-nurture-muted mt-1">
            Here's what's happening with {family?.name || 'your family'} today
          </p>
        </div>
      </div>

      {/* ============ ZONE 1: BRIEFING STRIP ============ */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-gradient-to-r from-nurture-cream to-nurture-maize/20 border-none rounded-3xl overflow-hidden">
          <CardContent className="p-5">
            {/* Refresh button */}
            <div className="flex justify-end mb-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchBriefing(true)}
                disabled={briefingLoading}
                className="text-nurture-muted hover:text-nurture-navy"
                data-testid="refresh-briefing-btn"
              >
                <RefreshCw className={`w-4 h-4 ${briefingLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {briefingLoading && !briefing ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
              </div>
            ) : (
              <>
                {/* Briefing Cards Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {/* Overdue Tasks */}
                  <Link to="/school" className="block">
                    <div className={`p-4 rounded-2xl transition-all hover:scale-105 ${
                      briefing?.overdue_tasks?.length > 0 ? 'bg-red-50 border border-red-200' : 'bg-white/70'
                    }`} data-testid="overdue-tasks-card">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className={`w-5 h-5 ${briefing?.overdue_tasks?.length > 0 ? 'text-red-500' : 'text-nurture-muted'}`} />
                        <span className="text-sm font-medium text-nurture-navy">Overdue</span>
                      </div>
                      <p className={`text-2xl font-bold ${briefing?.overdue_tasks?.length > 0 ? 'text-red-600' : 'text-nurture-sage'}`}>
                        {briefing?.overdue_tasks?.length || 0}
                      </p>
                      {briefing?.overdue_tasks?.length > 0 && (
                        <p className="text-xs text-red-500 mt-1 truncate">
                          {briefing.overdue_tasks[0]?.title}
                        </p>
                      )}
                    </div>
                  </Link>

                  {/* Tonight's Dinner */}
                  <Link to="/meals" className="block">
                    <div className="p-4 rounded-2xl bg-white/70 transition-all hover:scale-105" data-testid="tonight-dinner-card">
                      <div className="flex items-center gap-2 mb-2">
                        <UtensilsCrossed className="w-5 h-5 text-orange-500" />
                        <span className="text-sm font-medium text-nurture-navy">Tonight</span>
                      </div>
                      <p className="text-sm font-semibold text-nurture-navy truncate">
                        {briefing?.tonight_dinner || 'Not planned'}
                      </p>
                      {!briefing?.tonight_dinner && (
                        <p className="text-xs text-nurture-muted mt-1">Tap to plan</p>
                      )}
                    </div>
                  </Link>

                  {/* Tomorrow Prep */}
                  <Link to="/school?tab=tomorrow" className="block">
                    <div className="p-4 rounded-2xl bg-white/70 transition-all hover:scale-105" data-testid="tomorrow-prep-card">
                      <div className="flex items-center gap-2 mb-2">
                        <Moon className="w-5 h-5 text-purple-500" />
                        <span className="text-sm font-medium text-nurture-navy">Tomorrow</span>
                      </div>
                      <p className={`text-lg font-bold ${briefing?.tomorrow_prep_items?.length > 0 ? 'text-purple-600' : 'text-nurture-sage'}`}>
                        {briefing?.tomorrow_prep_items?.length > 0 ? `${briefing.tomorrow_prep_items.length} items` : 'All clear'}
                      </p>
                    </div>
                  </Link>

                  {/* AI Daily Insight Button */}
                  <div>
                    <Button
                      onClick={getAiDailyInsight}
                      disabled={aiLoading}
                      className="w-full h-full min-h-[80px] bg-gradient-to-br from-nurture-terracotta to-nurture-terracotta-hover text-white rounded-2xl flex flex-col items-center justify-center gap-2 shadow-nurture-button hover:shadow-lg transition-all"
                      data-testid="ai-insight-btn"
                    >
                      <Sparkles className={`w-5 h-5 ${aiLoading ? 'animate-pulse' : ''}`} />
                      <span className="text-sm font-medium">
                        {aiLoading ? 'Thinking...' : 'Get today\'s insight'}
                      </span>
                    </Button>
                  </div>
                </div>

                {/* AI Insight Expanded Card */}
                {insightExpanded && aiInsight && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4"
                  >
                    <Card className="bg-white border-nurture-terracotta/20 rounded-2xl">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-nurture-terracotta/10 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-4 h-4 text-nurture-terracotta" />
                          </div>
                          <div className="flex-1">
                            <p className="text-nurture-navy leading-relaxed" data-testid="ai-insight-text">
                              {aiInsight}
                            </p>
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => confirmInsight('accepted')}
                                className="bg-nurture-sage hover:bg-nurture-sage-hover text-white rounded-full text-xs"
                                data-testid="accept-insight-btn"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Helpful
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => confirmInsight('ignored')}
                                className="rounded-full text-xs"
                                data-testid="dismiss-insight-btn"
                              >
                                <X className="w-3 h-3 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Daily Affirmation */}
                {briefing?.daily_affirmation && (
                  <p className="text-sm italic text-nurture-navy/70 text-center" data-testid="daily-affirmation">
                    "{briefing.daily_affirmation}"
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* ============ ZONE 2: HUB (Existing Content) ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chaos Meter - Now Tappable */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <Card 
            className="h-full rounded-3xl shadow-nurture hover:shadow-nurture-hover transition-all cursor-pointer group"
            onClick={handleChaosClick}
            data-testid="chaos-meter-card"
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-nurture-navy font-heading">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-nurture-terracotta" />
                  Family Chaos Meter
                </div>
                <ChevronRight className="w-4 h-4 text-nurture-muted group-hover:text-nurture-navy transition-colors" />
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="text-center mb-4">
                <span className={`text-4xl font-bold ${chaosInfo.color}`}>
                  {chaosInfo.isPlaceholder ? '—' : `${chaosLevel}%`}
                </span>
                <p className={`text-sm font-medium ${chaosInfo.color} mt-1`}>{chaosInfo.label}</p>
                <p className="text-xs text-nurture-muted mt-0.5" data-testid="chaos-open-count">{chaosSublabel}</p>
                {briefing?.chaos_show_share && (
                  <p className="text-xs text-nurture-muted mt-0.5" data-testid="chaos-user-share">
                    You're carrying {briefing.chaos_user_share}%
                  </p>
                )}
              </div>
              <Progress value={chaosInfo.isPlaceholder ? 0 : chaosLevel} className="h-3 rounded-full" />
              <p className="text-xs text-nurture-muted text-center mt-3 group-hover:text-nurture-navy transition-colors">
                {chaosInfo.isPlaceholder ? 'Tap to view Load Map' : 'Tap to view Load Map details'}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Links Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card className="h-full rounded-3xl shadow-nurture">
            <CardHeader className="pb-2">
              <CardTitle className="text-nurture-navy font-heading">Quick Access</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {quickLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="group"
                    >
                      <div className="p-3 rounded-2xl bg-nurture-cream/50 hover:bg-nurture-cream transition-all text-center">
                        <div className={`w-10 h-10 mx-auto rounded-xl ${link.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <p className="text-xs font-medium text-nurture-navy">{link.label}</p>
                        {link.count > 0 && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {link.count}
                          </Badge>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="rounded-3xl shadow-nurture">
          <CardContent className="p-4">
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/brain-dump">
                <Button variant="outline" className="rounded-full">
                  <Brain className="w-4 h-4 mr-2" />
                  Quick Brain Dump
                </Button>
              </Link>
              <Link to="/weekly-reset">
                <Button variant="outline" className="rounded-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  Weekly Reset
                </Button>
              </Link>
              <Link to="/handoff">
                <Button variant="outline" className="rounded-full">
                  <Users className="w-4 h-4 mr-2" />
                  Create Handoff
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
